import axios from 'axios';

// Direct backend connection for development
const API_BASE_URL = 'http://localhost:8000/api/announcements';

// Set up axios defaults
axios.defaults.headers.common['Content-Type'] = 'application/json';

// Helper: get CSRF token from cookie
const getCsrfToken = () => {
  try {
    const m = document.cookie.match(/csrftoken=([^;]+)/);
    return m ? m[1] : '';
  } catch {
    return '';
  }
};

// Enhanced authentication with session + token fallback + CSRF for unsafe methods
const makeAuthenticatedRequest = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);

  // Try token-based request first
  const token = localStorage.getItem('access_token');
  if (token) {
    try {
      const tokenResponse = await axios({
        ...options,
        url,
        withCredentials: false,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
          ...options.headers,
        },
      });
      return tokenResponse;
    } catch (tokenError) {
      console.log('Token-based request failed:', tokenError.response?.status);
    }
  }

  // Try session-based request as fallback
  try {
    const sessionResponse = await axios({
      ...options,
      url,
      withCredentials: true,
      headers: {
        'Content-Type': 'application/json',
        ...(needsCsrf ? { 'X-CSRFToken': getCsrfToken() } : {}),
        ...options.headers,
      },
    });
    return sessionResponse;
  } catch (sessionError) {
    const status = sessionError.response?.status;
    if (status === 401 || status === 403) {
      // Return a graceful failure for unauthenticated users
      return {
        data: { results: [], count: 0 },
        status: 200
      };
    }
    throw sessionError;
  }
};

/**
 * Announcements API Service
 */
export const announcementsApi = {
  /**
   * Get all announcements for the current user
   */
  getAnnouncements: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/`, {
        method: 'GET'
      });
      return {
        success: true,
        data: response.data.results || response.data,
        pagination: response.data.results ? {
          count: response.data.count,
          next: response.data.next,
          previous: response.data.previous
        } : null
      };
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch announcements',
        data: []
      };
    }
  },

  /**
   * Get wishlist deadline announcements
   */
  getWishlistDeadlines: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/wishlist-deadlines/`, {
        method: 'GET'
      });
      return {
        success: true,
        data: response.data.results || response.data
      };
    } catch (error) {
      console.error('Failed to fetch wishlist deadlines:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch wishlist deadlines',
        data: []
      };
    }
  },

  /**
   * Get announcement statistics
   */
  getAnnouncementStats: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/stats/`, {
        method: 'GET'
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch announcement stats:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch announcement stats',
        data: {
          urgent_announcements: 0,
          wishlist_deadlines: 0,
          total_active: 0
        }
      };
    }
  },

  /**
   * Dismiss an announcement
   */
  dismissAnnouncement: async (announcementId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/${announcementId}/dismiss/`, {
        method: 'POST',
        data: {}
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Failed to dismiss announcement:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to dismiss announcement'
      };
    }
  },

  /**
   * Mark an announcement as read
   */
  markAnnouncementAsRead: async (announcementId) => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/${announcementId}/mark-read/`, {
        method: 'POST',
        data: {}
      });
      return {
        success: true,
        message: response.data.message
      };
    } catch (error) {
      console.error('Failed to mark announcement as read:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark announcement as read'
      };
    }
  },

  /**
   * Mark all announcements as read
   */
  markAllAnnouncementsAsRead: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/mark-all-read/`, {
        method: 'POST',
        data: {}
      });
      return {
        success: true,
        message: response.data.message,
        marked_count: response.data.marked_count
      };
    } catch (error) {
      console.error('Failed to mark all announcements as read:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to mark all announcements as read'
      };
    }
  },

  /**
   * Get unread announcements count
   */
  getUnreadCount: async () => {
    try {
      const response = await makeAuthenticatedRequest(`${API_BASE_URL}/unread-count/`, {
        method: 'GET'
      });
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      return {
        success: false,
        error: error.response?.data?.error || 'Failed to fetch unread count',
        data: { unread_count: 0 }
      };
    }
  },

  /**
   * Get featured announcements for home page (public endpoint)
   */
  getFeaturedAnnouncements: async () => {
    try {
      // This is a public endpoint, so no authentication needed
      const response = await axios.get(`${API_BASE_URL}/featured/`);
      return {
        success: true,
        data: response.data.results || response.data,
        metadata: {
          count: response.data.count,
          featured_count: response.data.featured_count,
          selection_criteria: response.data.selection_criteria,
          selected_at: response.data.selected_at
        }
      };
    } catch (error) {
      console.error('Failed to fetch featured announcements:', error);
      return {
        success: false,
        error: error.response?.data?.message || 'Failed to fetch featured announcements',
        data: []
      };
    }
  }
};

export default announcementsApi;
